import { useCallback, useEffect, useState } from 'react'
import { ClipboardCheck, Loader2, UserCheck } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { Button } from '../ui/kit'
import AusweisScanPanel from './AusweisScanPanel'
import type { Booking } from '../data/model'
import { oeffentlicheUrl } from '../lib/urls'

/**
 * Der Online-Check-in, aus Sicht des Vermieters.
 *
 * Er erzeugt hier einen Link und schickt ihn dem Gast selbst — per Mail, per
 * WhatsApp, wie auch immer. StaySuite verschickt ihn nicht: der Vermieter kopiert
 * ihn und setzt ihn in seine eigene Nachricht. Der Gast füllt den Meldeschein aus,
 * bevor er anreist, und sagt dabei, wann er ungefähr eintrifft. Was zurückkommt,
 * steht hier und auf der Tagesübersicht.
 *
 * WARUM DER LINK NICHT AUTOMATISCH BEI JEDER BUCHUNG ENTSTEHT: eine Anfrage ist keine
 * Anreise. Ein Meldeschein für eine Buchung, die nie zustande kommt, ist eine
 * Aufforderung an einen Fremden, seine Ausweisnummer in ein Formular zu tippen. Der
 * Link entsteht deshalb, wenn der Vermieter ihn braucht — und dann für immer derselbe,
 * damit eine zweite Nachricht nicht einen zweiten Meldeschein aufmacht.
 */

interface Registration {
  id: string
  token: string
  status: 'open' | 'submitted' | 'checked_in'
  arrival_time: string | null
  note: string | null
  submitted_at: string | null
}

export default function CheckinCard({ booking }: { booking: Booking }) {
  const { lang, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [reg, setReg] = useState<Registration | null>(null)
  const [people, setPeople] = useState(0)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!store.live || !supabase) return
    const { data } = await supabase.from('guest_registrations')
      .select('id, token, status, arrival_time, note, submitted_at')
      .eq('booking_id', booking.id).maybeSingle()
    setReg((data as Registration | null) ?? null)
    if (data) {
      const { count } = await supabase.from('guest_registration_people')
        .select('id', { count: 'exact', head: true })
        .eq('registration_id', (data as Registration).id)
      setPeople(count ?? 0)
    }
  }, [booking.id, store.live])

  useEffect(() => { void load() }, [load])

  if (booking.kind === 'block') return null

  const url = reg ? oeffentlicheUrl(`/checkin/${reg.token}`) : null

  /*
   * Gibt es die Buchung in der Datenbank überhaupt schon?
   *
   * Eine im Kalender aufgezogene Buchung lebt bis zum Speichern nur im Blatt — der
   * Store kennt sie nicht, die Datenbank erst recht nicht. Ein Meldeschein hängt aber
   * per Fremdschlüssel an ihr. Wer den Link vorher erzeugen wollte, bekam die rohe
   * Meldung „violates foreign key constraint guest_registrations_booking_id_fkey"
   * vorgesetzt — eine Zeile, die niemandem sagt, was zu tun ist.
   *
   * Der Knopf verschwindet deshalb nicht, er erklärt: erst speichern, dann Link. Ein
   * fehlender Knopf wirft die Frage auf, warum er fehlt.
   */
  const savedYet = store.db.bookings.some((b) => b.id === booking.id)

  const create = async (): Promise<Registration | null> => {
    setBusy(true); setError(null)
    try {
      if (!supabase) throw new Error('no_backend')
      const { data, error: e } = await supabase.from('guest_registrations')
        .insert({ tenant_id: store.db.tenant.id, booking_id: booking.id })
        .select('id, token, status, arrival_time, note, submitted_at').single()
      if (e) throw e
      setReg(data as Registration)
      return data as Registration
    } catch (err) {
      // Datenbankmeldungen sind für Entwickler geschrieben. Was hier ankommt, muss
      // sagen, was der Vermieter tun kann — der Rest gehört ins Protokoll.
      const raw = String((err as { message?: string })?.message ?? err)
      console.error('[StaySuite] Check-in-Link:', raw)
      setError(
        raw.includes('guest_registrations_booking_id_fkey')
          ? (de
              ? 'Diese Buchung ist noch nicht gespeichert. Bitte zuerst speichern, danach lässt sich der Link erzeugen.'
              : 'This booking has not been saved yet. Save it first, then create the link.')
        : raw.includes('duplicate key') || raw.includes('unique')
          ? (de ? 'Für diese Buchung gibt es bereits einen Meldeschein.'
                : 'This booking already has a registration form.')
        : raw === 'no_backend'
          ? (de ? 'Keine Verbindung zum Server.' : 'No connection to the server.')
        : (de ? 'Der Link konnte nicht erzeugt werden. Bitte später noch einmal versuchen.'
              : 'The link could not be created. Please try again later.'),
      )
    }
    setBusy(false)
    return null
  }

  if (!store.live) {
    return (
      <p className="text-xs text-muted">
        {de
          ? 'Der Online-Check-in steht im angemeldeten Konto zur Verfügung.'
          : 'Online check-in is available in a signed-in account.'}
      </p>
    )
  }

  return (
    <section className="bg-surface-2 rounded-2xl p-3 space-y-2.5">
      <div className="flex items-baseline justify-between">
        <h3 className="font-medium text-sm flex items-center gap-1.5">
          <ClipboardCheck size={15} />
          {de ? 'Online-Check-in' : 'Online check-in'}
        </h3>
        {reg && (
          <span className={
            reg.status === 'open' ? 'text-xs text-warn' : 'text-xs text-ok'
          }>
            {reg.status === 'open'
              ? (de ? 'Noch nicht ausgefüllt' : 'Not filled in yet')
              : (de ? `Meldeschein liegt vor · ${people} ${people === 1 ? 'Person' : 'Personen'}`
                    : `Registration received · ${people} ${people === 1 ? 'person' : 'people'}`)}
          </span>
        )}
      </div>

      {/* Das Wichtigste zuerst: wann kommt er? Danach fragt der Vermieter am
          Anreisetag als Erstes, und bisher stand die Antwort in keiner Zeile. */}
      {reg?.arrival_time && (
        <p className="text-sm">
          <UserCheck size={14} className="inline mr-1.5 -mt-0.5 text-ok" />
          {de ? 'Ankunft gegen ' : 'Arriving around '}
          <strong>{reg.arrival_time}</strong>
          {reg.submitted_at && (
            <span className="text-muted text-xs">
              {' · '}{de ? 'gemeldet am ' : 'submitted '}{formatDate(reg.submitted_at.slice(0, 10))}
            </span>
          )}
        </p>
      )}
      {reg?.note && (
        <p className="text-sm bg-surface rounded-xl px-3 py-2">{reg.note}</p>
      )}

      {url ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <input
              readOnly value={url} onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 text-xs bg-surface rounded-xl px-3 py-2"
            />
            <Button size="sm" variant="secondary" onClick={() => {
              void navigator.clipboard?.writeText(url)
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1500)
            }}>
              {copied ? (de ? 'Kopiert' : 'Copied') : (de ? 'Kopieren' : 'Copy')}
            </Button>
          </div>
          {/* Wer den Link verschickt — und dass ein zweites Mal nichts kaputt macht. */}
          <p className="text-xs text-muted">
            {de
              ? 'Diesen Link selbst an den Gast schicken — per Mail, per Nachricht, wie es passt. Er bleibt derselbe: ein zweites Mal schicken erzeugt keinen zweiten Meldeschein.'
              : 'Send this link to the guest yourself — by mail, by message, whichever suits. It stays the same: sending it twice does not create a second registration form.'}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Button size="sm" variant="secondary" disabled={busy || !savedYet} onClick={create}>
            {busy && <Loader2 size={15} className="animate-spin" />}
            {de ? 'Check-in-Link erzeugen' : 'Create check-in link'}
          </Button>
          <p className="text-xs text-muted">
            {!savedYet
              ? (de
                  ? 'Erst speichern: der Meldeschein hängt an der Buchung, und die gibt es noch nicht.'
                  : 'Save first: the registration form belongs to the booking, and that does not exist yet.')
              : (de
                  ? 'Der Gast trägt darüber den Meldeschein und seine ungefähre Ankunftszeit ein — ohne Konto, auf dem Handy.'
                  : 'The guest fills in the registration form and their expected arrival time — no account, on their phone.')}
          </p>
        </div>
      )}

      {/* Der Scanner, nur in der App — im Browser gibt das Bauteil `null`
          zurück. Er sitzt hier und nicht auf der Gastseite, weil der Vermieter
          bei der Anreise den Ausweis in der Hand hat und der Gast, der vorab
          ausfüllt, meist nicht. Siehe `docs/adr/004`. */}
      {reg && <AusweisScanPanel registrationId={reg.id} onSaved={() => void load()} />}

      {error && <p className="text-xs text-danger">{error}</p>}
    </section>
  )
}
